import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, ShieldCheck, Scan, Wifi, Car, Phone,
  BarChart3, BrainCircuit, Lock, ArrowRight, Check, Play, Star,
  Users, Building2, Eye, Radio, Server, Activity,
  Cpu, Satellite, AlertTriangle
} from 'lucide-react';
import { useRegion } from '../contexts/RegionContext';
import RegionSelector from '../components/RegionSelector';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const SecureSphereLanding = () => {
  const navigate = useNavigate();
  const { t, language } = useRegion();
  const [stats, setStats] = useState({
    threats_blocked: 10847293,
    devices_protected: 52841,
    detection_rate: 99.97,
    response_time: 8
  });
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch live stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      // Using default stats
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const features = [
    { id: 'url-scanner', icon: Scan, title: "URL Scanner", desc: "Real-time malicious URL detection with ML-powered threat analysis", color: "from-amber-500 to-orange-600" },
    { id: 'sms-phishing', icon: Phone, title: "SMS Phishing Analysis", desc: "Detect smishing attacks with AI-driven content analysis", color: "from-orange-500 to-red-500" },
    { id: 'iot-security', icon: Wifi, title: "IoT Security", desc: "Comprehensive device registry and vulnerability management", color: "from-yellow-500 to-amber-500" },
    { id: 'automotive', icon: Car, title: "Automotive Security", desc: "V2X, CAN bus, and ECU protection for connected vehicles", color: "from-amber-400 to-orange-500" },
    { id: 'gsm-fraud', icon: Radio, title: "GSM Fraud Detection", desc: "Detect SIM swap, IMSI catchers, and telecom fraud", color: "from-orange-400 to-red-500" },
    { id: 'telecom', icon: Server, title: "Telecom Integration", desc: "SS7, Diameter, and GTP protocol security monitoring", color: "from-yellow-400 to-amber-500" },
    { id: 'csp-ops', icon: Building2, title: "CSP Operations", desc: "Carrier-grade security operations center integration", color: "from-amber-500 to-orange-500" },
    { id: 'ai-agents', icon: BrainCircuit, title: "AI Security Agents", desc: "Autonomous threat hunting and incident response", color: "from-orange-500 to-amber-600" },
    { id: 'analytics', icon: BarChart3, title: "Analytics & Reports", desc: "Executive dashboards and compliance reporting", color: "from-yellow-500 to-orange-500" },
    { id: 'surveillance', icon: Eye, title: "Surveillance IoT", desc: "Camera and sensor network security monitoring", color: "from-amber-400 to-yellow-500" },
    { id: 'mobile', icon: Shield, title: "Mobile Protection", desc: "Enterprise MDM and mobile threat defense", color: "from-orange-400 to-amber-500" },
    { id: '5g-iot', icon: Satellite, title: "5G IoT Compliance", desc: "Next-gen network security and compliance", color: "from-yellow-400 to-orange-500" }
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Chen",
      role: "CISO, TelcoMax Global",
      image: "https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwaGVhZHNob3R8ZW58MHx8fHwxNzc1NTc4NjcwfDA&ixlib=rb-4.1.0&q=85",
      quote: "SecureSphere transformed our security posture. The GSM fraud detection alone saved us $4.2M in the first quarter."
    },
    {
      name: "Michael Torres",
      role: "VP Security, AutoNet Systems",
      image: "https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwaGVhZHNob3R8ZW58MHx8fHwxNzc1NTc4NjcwfDA&ixlib=rb-4.1.0&q=85",
      quote: "The automotive security module is unparalleled. We've achieved ISO 21434 compliance 6 months ahead of schedule."
    }
  ];

  return (
    <div className={`min-h-screen bg-[#1a1a1a] text-white overflow-x-hidden ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <SEO
        title="SecureSphere - Telco-Grade Enterprise Security | Infuse.AI"
        description="Complete protection for phones, tablets, IoT devices, automotive, telecom networks, and AI workloads. Defend against financial fraud, identity theft, GSM attacks, and prompt injection with AI-powered security."
        keywords="enterprise security, telecom security, IoT security, AI security, GSM fraud detection, automotive security, prompt injection detection, SIEM, SOC, SecureSphere, Infuse.AI"
        canonical="https://infuse-ai.in"
      />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#1a1a1a]/95 backdrop-blur-xl border-b border-white/10' : 'bg-[#1a1a1a]/80 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-shrink-0 mr-8">
              <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
              <div>
                <span className="text-xl font-bold whitespace-nowrap">
                  <span className="text-white">Infuse</span>
                  <span className="text-[#F26419]">.AI</span>
                  <span className="text-neutral-400 text-xs align-top">™</span>
                </span>
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-6">
              <a href="#features" className="text-sm text-neutral-300 hover:text-white transition whitespace-nowrap">{t('features')}</a>
              <a href="#solutions" className="text-sm text-neutral-300 hover:text-white transition whitespace-nowrap">{t('solutions')}</a>
              <a href="#security" className="text-sm text-neutral-300 hover:text-white transition whitespace-nowrap">{t('security')}</a>
              <a href="#compliance" className="text-sm text-neutral-300 hover:text-white transition whitespace-nowrap">{t('compliance')}</a>
              <button onClick={() => navigate('/about')} className="text-sm text-neutral-300 hover:text-white transition whitespace-nowrap">{t('about')}</button>
              <button onClick={() => navigate('/live-metrics')} data-testid="nav-live-demo" className="text-sm font-semibold text-green-400 hover:text-green-300 transition whitespace-nowrap flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />Live Demo</button>
              <button onClick={() => navigate('/admin')} className="text-sm text-neutral-500 hover:text-orange-400 transition whitespace-nowrap">Admin</button>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Region/Language Selector */}
              <RegionSelector />
              <button 
                data-testid="nav-login-btn"
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 bg-[#F26419] text-white font-semibold rounded-lg text-sm hover:bg-[#F7B731] hover:text-black transition-all"
              >
                {t('sign_in')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with custom security graphic */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2a1f1a] to-[#1a1a1a]" />
        
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(242,100,25,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(242,100,25,0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        {/* Floating Security Icons - representing all protection domains */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Phone/Mobile Protection */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="absolute top-[15%] right-[10%] w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F7B731]/20 to-[#F26419]/20 backdrop-blur-sm border border-orange-500/30 flex items-center justify-center animate-float-slow animate-glow-pulse"
          >
            <Phone className="w-8 h-8 text-orange-400" />
          </motion.div>
          
          {/* Smart City IoT */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{ delay: 0.7, duration: 1 }}
            className="absolute top-[25%] right-[25%] w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F7B731]/20 to-[#F26419]/20 backdrop-blur-sm border border-orange-500/30 flex items-center justify-center animate-float-medium"
          >
            <Building2 className="w-7 h-7 text-orange-400" />
          </motion.div>
          
          {/* WiFi/Network Security */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="absolute top-[40%] right-[8%] w-12 h-12 rounded-xl bg-gradient-to-br from-[#F7B731]/20 to-[#F26419]/20 backdrop-blur-sm border border-orange-500/30 flex items-center justify-center animate-float-fast"
          >
            <Wifi className="w-6 h-6 text-orange-400" />
          </motion.div>
          
          {/* Financial/Identity Protection */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="absolute top-[55%] right-[20%] w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F7B731]/20 to-[#F26419]/20 backdrop-blur-sm border border-orange-500/30 flex items-center justify-center animate-float-slow"
            style={{ animationDelay: '1s' }}
          >
            <Shield className="w-7 h-7 text-orange-400" />
          </motion.div>
          
          {/* GSM/Telecom */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="absolute top-[70%] right-[12%] w-12 h-12 rounded-xl bg-gradient-to-br from-[#F7B731]/20 to-[#F26419]/20 backdrop-blur-sm border border-orange-500/30 flex items-center justify-center animate-float-medium"
            style={{ animationDelay: '0.5s' }}
          >
            <Radio className="w-6 h-6 text-orange-400" />
          </motion.div>

          {/* Tablet/Enterprise */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="absolute top-[20%] right-[40%] w-12 h-12 rounded-xl bg-gradient-to-br from-[#F7B731]/20 to-[#F26419]/20 backdrop-blur-sm border border-orange-500/30 flex items-center justify-center animate-float-fast"
            style={{ animationDelay: '2s' }}
          >
            <Cpu className="w-6 h-6 text-orange-400" />
          </motion.div>

          {/* Automotive/Car Security */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 0.6, x: 0 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="absolute top-[65%] right-[35%] w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F7B731]/15 to-[#F26419]/15 backdrop-blur-sm border border-orange-500/20 flex items-center justify-center animate-float-slow"
            style={{ animationDelay: '1.5s' }}
          >
            <Car className="w-7 h-7 text-amber-400/80" />
          </motion.div>

          {/* Surveillance Camera */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 0.6, x: 0 }}
            transition={{ delay: 1.7, duration: 1 }}
            className="absolute top-[10%] right-[30%] w-11 h-11 rounded-xl bg-gradient-to-br from-[#F7B731]/15 to-[#F26419]/15 backdrop-blur-sm border border-orange-500/20 flex items-center justify-center animate-float-medium"
            style={{ animationDelay: '0.8s' }}
          >
            <Eye className="w-5 h-5 text-amber-400/80" />
          </motion.div>

          {/* Satellite/Global */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 0.5, y: 0 }}
            transition={{ delay: 1.9, duration: 1 }}
            className="absolute top-[45%] right-[38%] w-10 h-10 rounded-lg bg-gradient-to-br from-[#F7B731]/15 to-[#F26419]/15 backdrop-blur-sm border border-orange-500/20 flex items-center justify-center animate-float-fast"
            style={{ animationDelay: '1.2s' }}
          >
            <Satellite className="w-5 h-5 text-amber-400/70" />
          </motion.div>

          {/* AI Security Brain */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.7, scale: 1 }}
            transition={{ delay: 2.1, duration: 1 }}
            className="absolute top-[35%] right-[18%] w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-[#F26419]/20 backdrop-blur-sm border border-purple-500/30 flex items-center justify-center animate-float-slow animate-glow-pulse"
            style={{ animationDelay: '0.3s' }}
          >
            <BrainCircuit className="w-7 h-7 text-purple-400" />
          </motion.div>

          {/* Connection lines */}
          <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.15 }}>
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F7B731" />
                <stop offset="100%" stopColor="#F26419" />
              </linearGradient>
            </defs>
            <line x1="85%" y1="20%" x2="75%" y2="30%" stroke="url(#lineGradient)" strokeWidth="1" />
            <line x1="75%" y1="30%" x2="90%" y2="45%" stroke="url(#lineGradient)" strokeWidth="1" />
            <line x1="90%" y1="45%" x2="80%" y2="60%" stroke="url(#lineGradient)" strokeWidth="1" />
            <line x1="80%" y1="60%" x2="88%" y2="75%" stroke="url(#lineGradient)" strokeWidth="1" />
            <line x1="60%" y1="25%" x2="75%" y2="30%" stroke="url(#lineGradient)" strokeWidth="1" />
            {/* New connections for added icons */}
            <line x1="65%" y1="70%" x2="80%" y2="60%" stroke="url(#lineGradient)" strokeWidth="1" />
            <line x1="70%" y1="15%" x2="75%" y2="30%" stroke="url(#lineGradient)" strokeWidth="1" />
            <line x1="62%" y1="50%" x2="80%" y2="60%" stroke="url(#lineGradient)" strokeWidth="1" />
          </svg>

          {/* Central Shield Graphic */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 1.2 }}
            className="absolute top-[30%] right-[15%] w-32 h-32"
          >
            <div className="relative w-full h-full">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#F7B731]/10 to-[#F26419]/10 blur-2xl" />
              <div className="absolute inset-2 rounded-full bg-gradient-to-br from-[#F7B731]/20 to-[#F26419]/20 backdrop-blur-xl border border-orange-500/30 flex items-center justify-center">
                <ShieldCheck className="w-12 h-12 text-orange-500" />
              </div>
              {/* Pulsing ring */}
              <div className="absolute inset-0 rounded-full border-2 border-orange-500/30 animate-ping" style={{ animationDuration: '3s' }} />
            </div>
          </motion.div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial="hidden" 
              animate="visible" 
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.div variants={fadeInUp}>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs uppercase tracking-[0.2em] font-semibold">
                  <Lock className="w-3.5 h-3.5" />
                  {t('hero_tagline')}
                </span>
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp}
                className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none"
              >
                {t('hero_title_1')}
                <span className="block bg-gradient-to-r from-[#F7B731] to-[#F26419] bg-clip-text text-transparent">
                  {t('hero_title_2')}
                </span>
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-lg text-neutral-300 leading-relaxed max-w-xl"
              >
                {t('hero_desc')}
              </motion.p>

              {/* USP Tags */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-2">
                {[
                  { key: 'mobile_security', label: t('mobile_security') },
                  { key: 'financial_fraud', label: t('financial_fraud') },
                  { key: 'identity_protection', label: t('identity_protection') },
                  { key: 'iot_smart_city', label: t('iot_smart_city') },
                  { key: 'gsm_telecom', label: t('gsm_telecom') },
                  { key: 'enterprise', label: t('enterprise') },
                  { key: 'ai_security', label: t('ai_security') || 'AI Security' }
                ].map((tag) => (
                  <span key={tag.key} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs text-neutral-300">
                    {tag.label}
                  </span>
                ))}
              </motion.div>
              
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <button 
                  data-testid="hero-cta-demo"
                  onClick={() => navigate('/login')}
                  className="group px-8 py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-full text-lg hover:shadow-[0_0_40px_rgba(242,100,25,0.5)] transition-all transform hover:-translate-y-1 flex items-center gap-2"
                >
                  {t('start_trial')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button 
                  data-testid="hero-cta-watch"
                  onClick={() => navigate('/demo')}
                  className="px-8 py-4 bg-white/5 border border-white/20 text-white font-medium rounded-full text-lg hover:bg-white/10 transition-all flex items-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  {t('watch_demo')}
                </button>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map((i) => (
                    <div key={`user-${i}`} className="w-10 h-10 rounded-full bg-gradient-to-r from-[#F7B731] to-[#F26419] border-2 border-[#1a1a1a] flex items-center justify-center">
                      <Users className="w-4 h-4 text-black" />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{t('trusted_by')}</p>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={`star-${i}`} className="w-4 h-4 fill-orange-500 text-orange-500" />
                    ))}
                    <span className="text-xs text-neutral-400 ml-1">4.9/5 Rating</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Live Dashboard Preview */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5 shadow-2xl max-w-sm ml-auto">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-[#F7B731] to-[#F26419] flex items-center justify-center">
                      <ShieldCheck className="w-4.5 h-4.5 text-black" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">Security Status</h3>
                      <p className="text-[10px] text-neutral-500">Real-time monitoring</p>
                    </div>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[10px] font-semibold">
                    Protected
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-black/30 rounded-lg p-3 border border-white/5">
                    <p className="text-2xl font-bold text-white">{(stats.threats_blocked / 1000000).toFixed(1)}M+</p>
                    <p className="text-[10px] text-neutral-500 mt-0.5">Threats Blocked</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 border border-white/5">
                    <p className="text-2xl font-bold text-white">{(stats.devices_protected / 1000).toFixed(0)}K+</p>
                    <p className="text-[10px] text-neutral-500 mt-0.5">Devices Protected</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-neutral-400">Detection Rate</span>
                      <span className="text-white font-semibold">{stats.detection_rate}%</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#F7B731] to-[#F26419] rounded-full transition-all duration-1000"
                        style={{ width: `${stats.detection_rate}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-neutral-400">Response Time</span>
                      <span className="text-white font-semibold">&lt;{stats.response_time}ms</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"
                        style={{ width: '95%' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[10px] text-neutral-500">
                      <Activity className="w-3.5 h-3.5 text-green-400" />
                      <span>All systems operational</span>
                    </div>
                    <button 
                      onClick={() => navigate('/login')}
                      className="text-[10px] text-orange-400 hover:text-orange-300 transition flex items-center gap-1"
                    >
                      View Dashboard <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Floating alert */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute -bottom-4 -left-4 bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-white">Threat Neutralized</p>
                    <p className="text-[10px] text-neutral-500">SQL Injection blocked • 2s ago</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gradient-to-r from-[#F7B731] to-[#F26419] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-4xl font-black text-black">10B+</p>
              <p className="text-sm text-black/70 font-medium">Threats Blocked</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-black">50M+</p>
              <p className="text-sm text-black/70 font-medium">Devices Protected</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-black">99.99%</p>
              <p className="text-sm text-black/70 font-medium">Detection Rate</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-black">&lt;10ms</p>
              <p className="text-sm text-black/70 font-medium">Response Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500"
            >
              Complete Security Suite
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold tracking-tight mt-4"
            >
              Enterprise-Grade
              <span className="bg-gradient-to-r from-[#F7B731] to-[#F26419] bg-clip-text text-transparent"> Protection</span>
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-neutral-400 mt-4 max-w-2xl mx-auto"
            >
              12 integrated security modules working in harmony to protect your entire digital ecosystem
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {features.map((feature) => (
              <motion.div
                key={feature.id}
                variants={fadeInUp}
                data-testid={`feature-card-${feature.id}`}
                className="group relative bg-[#242424] rounded-xl p-6 border border-white/5 hover:border-orange-500/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#F7B731]/5 to-[#F26419]/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-black" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-24 bg-[#242424]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.span 
                variants={fadeInUp}
                className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500"
              >
                Industry Solutions
              </motion.span>
              <motion.h2 
                variants={fadeInUp}
                className="text-4xl sm:text-5xl font-bold tracking-tight mt-4"
              >
                Built for
                <span className="bg-gradient-to-r from-[#F7B731] to-[#F26419] bg-clip-text text-transparent"> Critical Infrastructure</span>
              </motion.h2>
              <motion.p 
                variants={fadeInUp}
                className="text-neutral-400 mt-4 mb-8"
              >
                SecureSphere is trusted by telecommunications providers, automotive manufacturers, 
                and enterprises worldwide to protect their most critical assets.
              </motion.p>
              
              <motion.div variants={fadeInUp} className="space-y-4">
                {[
                  { icon: Radio, text: "Telecommunications & CSP Networks" },
                  { icon: Car, text: "Automotive & Connected Vehicles" },
                  { icon: Wifi, text: "IoT & Smart Infrastructure" },
                  { icon: Building2, text: "Enterprise & Government" }
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-4 p-4 bg-black/30 rounded-xl border border-white/5">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-[#F7B731] to-[#F26419] flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-black" />
                    </div>
                    <span className="font-medium text-white">{item.text}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1764983253036-edd0f55af679?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxhdXRvbm9tb3VzJTIwdmVoaWNsZSUyMGZ1dHVyZXxlbnwwfHx8fDE3NzU1Nzg2ODV8MA&ixlib=rb-4.1.0&q=85"
                alt="Automotive Security"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent rounded-2xl" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-black/80 backdrop-blur-xl rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-white">Automotive Security Active</p>
                      <p className="text-xs text-neutral-500">V2X • CAN Bus • ECU Protection</p>
                    </div>
                    <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500"
            >
              Trusted Worldwide
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold tracking-tight mt-4"
            >
              What Security Leaders Say
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={`testimonial-${testimonial.name}-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-[#242424] rounded-2xl p-8 border border-white/5"
              >
                <div className="flex items-center gap-1 mb-6">
                  {[1,2,3,4,5].map((i) => (
                    <Star key={`star-${i}`} className="w-5 h-5 fill-orange-500 text-orange-500" />
                  ))}
                </div>
                <p className="text-lg text-neutral-300 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-neutral-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-24 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.span variants={fadeInUp} className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500">Security Architecture</motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold tracking-tight mt-4">Multi-Layered Protection</motion.h2>
            <motion.p variants={fadeInUp} className="text-neutral-400 mt-4 max-w-2xl mx-auto">
              SecureSphere deploys defense-in-depth across every layer — from network edge to AI workloads.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Zero Trust Architecture", desc: "Every request authenticated and authorized. No implicit trust." },
              { icon: Lock, title: "AES-256 Encryption", desc: "End-to-end encryption for data in transit and at rest." },
              { icon: BrainCircuit, title: "AI-Powered Detection", desc: "ML models trained on 10B+ events for 99.8% detection accuracy." },
              { icon: Activity, title: "Real-Time Response", desc: "8ms average threat response time with automated remediation." },
              { icon: AlertTriangle, title: "Prompt Injection Shield", desc: "Detect and block LLM prompt injection across 6 attack vectors." },
              { icon: Eye, title: "Behavioral Analytics", desc: "Continuous user and entity behavior analysis for anomaly detection." },
              { icon: Radio, title: "Protocol Security", desc: "SS7, Diameter, GTP, and 5G NR protocol-level threat monitoring." },
              { icon: Satellite, title: "Global Threat Intel", desc: "Feeds from 50+ threat intelligence providers updated hourly." }
            ].map((feature, index) => (
              <motion.div
                key={`feature-security-${feature.title}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="bg-[#242424] rounded-xl p-6 border border-white/10 hover:border-orange-500/30 transition-all"
              >
                <feature.icon className="w-8 h-8 text-orange-500 mb-3" />
                <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                <p className="text-sm text-neutral-500">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section id="compliance" className="py-24 bg-[#242424]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500"
            >
              Enterprise-Grade Security
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold tracking-tight mt-4"
            >
              SOC 2 Compliant • ISO 27001 Ready
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-neutral-400 mt-4 max-w-2xl mx-auto"
            >
              Your security data deserves the highest level of protection. We comply with global security and data protection regulations.
            </motion.p>
          </motion.div>

          {/* Compliance Badges */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12"
          >
            {[
              { name: "SOC 2 Type II", status: "Ready" },
              { name: "ISO 27001", status: "Ready" },
              { name: "GDPR", status: "Ready" },
              { name: "DPDP Act", status: "India" },
              { name: "PCI DSS", status: "Ready" },
              { name: "NIST", status: "Framework" }
            ].map((cert, index) => (
              <div key={`cert-${cert.name}-${index}`} className="bg-black/50 rounded-xl p-4 border border-white/10 text-center">
                <p className="text-sm font-semibold text-white">{cert.name}</p>
                <p className="text-xs text-orange-400">{cert.status}</p>
              </div>
            ))}
          </motion.div>

          {/* Global Compliance */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#1a1a1a] rounded-2xl p-8 border border-white/10"
          >
            <h3 className="text-xl font-semibold text-white mb-6 text-center">Global Security & Data Protection Compliance</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {[
                { region: "USA", standards: ["NIST", "SOC 2", "FISMA"] },
                { region: "Europe", standards: ["GDPR", "NIS2", "DORA"] },
                { region: "India", standards: ["DPDP Act", "IT Act", "CERT-In"] },
                { region: "UK", standards: ["UK GDPR", "DPA 2018", "Cyber Essentials"] },
                { region: "Middle East", standards: ["UAE PDPL", "Saudi PDPL", "ADHICS"] },
                { region: "Asia Pacific", standards: ["PDPA (SG)", "APPI (JP)", "PDPA (TH)"] }
              ].map((item, index) => (
                <div key={`region-${item.region}-${index}`} className="text-center">
                  <p className="text-sm font-semibold text-white mb-2">{item.region}</p>
                  <div className="space-y-1">
                    {item.standards.map((std, i) => (
                      <span key={std} className="text-xs bg-orange-500/10 text-orange-400 px-2 py-1 rounded block">{std}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Security Features */}
          <div className="grid md:grid-cols-4 gap-6 mt-8">
            {[
              { icon: Lock, title: "AES-256 Encryption", desc: "End-to-end encryption for all security data" },
              { icon: Shield, title: "Zero Trust Architecture", desc: "Every access request is verified and validated" },
              { icon: Users, title: "Role-Based Access", desc: "Granular permissions for authorized access" },
              { icon: Activity, title: "24/7 Monitoring", desc: "Continuous security monitoring and threat detection" }
            ].map((feature, index) => (
              <motion.div
                key={`feature-security-${feature.title}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-black/50 rounded-xl p-6 border border-white/10"
              >
                <feature.icon className="w-8 h-8 text-orange-500 mb-3" />
                <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                <p className="text-sm text-neutral-500">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm text-neutral-500 mt-8">
            SecureSphere undergoes regular third-party security audits and penetration testing. Our infrastructure is hosted on SOC 2 ready cloud providers with 99.99% uptime SLA.
          </p>
        </div>
      </section>

      {/* Pricing Notice Section - Redirects to sign-in */}
      <section id="pricing" className="py-16 bg-[#242424]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#1a1a1a] rounded-2xl p-8 border border-white/10"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#F7B731]/20 to-[#F26419]/20 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-orange-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Flexible Enterprise Pricing</h3>
            <p className="text-neutral-400 mb-6 max-w-md mx-auto">
              {t('pricing_after_login')}. Sign in to view plans tailored to your region and requirements.
            </p>
            <button 
              onClick={() => navigate('/login')}
              data-testid="pricing-signin-btn"
              className="px-8 py-3 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(242,100,25,0.4)] transition-all"
            >
              {t('sign_in')} to View Pricing
            </button>
          </motion.div>
        </div>
      </section>

      {/* Contact/CTA Section */}
      <section id="contact" className="py-24 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#F7B731]/10 to-[#F26419]/10 rounded-3xl p-12 border border-orange-500/20 text-center"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Secure Your Infrastructure?
            </h2>
            <p className="text-neutral-400 mb-8 max-w-2xl mx-auto">
              Schedule a personalized demo with our security experts and see how SecureSphere 
              can protect your organization.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button 
                data-testid="cta-demo-btn"
                onClick={() => navigate('/demo')}
                className="px-8 py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-full text-lg hover:shadow-[0_0_40px_rgba(242,100,25,0.5)] transition-all transform hover:-translate-y-1 flex items-center gap-2"
              >
                Schedule Demo
                <ArrowRight className="w-5 h-5" />
              </button>
              <a 
                data-testid="cta-contact-btn"
                href="mailto:info@infuse.net.in"
                className="px-8 py-4 bg-white/5 border border-white/20 text-white font-medium rounded-full text-lg hover:bg-white/10 transition-all"
              >
                Contact Sales
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-[#242424] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-10 w-10 rounded-lg" />
                <div>
                  <span className="text-lg font-bold">
                    <span className="text-white">Infuse</span>
                    <span className="text-[#F26419]">.AI</span>
                    <span className="text-neutral-400 text-xs align-top">™</span>
                  </span>
                </div>
              </div>
              <p className="text-sm text-neutral-500">
                SecureSphere - Your complete security companion. AI-powered threat analysis, verified security protocols, and enterprise-grade protection.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Features</h4>
              <ul className="space-y-2 text-sm text-neutral-500">
                <li><a href="#features" className="hover:text-white transition">URL Scanner</a></li>
                <li><a href="#features" className="hover:text-white transition">IoT Security</a></li>
                <li><a href="#features" className="hover:text-white transition">Automotive Security</a></li>
                <li><a href="#features" className="hover:text-white transition">GSM Fraud Detection</a></li>
                <li><a href="#features" className="hover:text-white transition">AI Security</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-neutral-500">
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition">About Us</button></li>
                <li><button onClick={() => navigate('/careers')} className="hover:text-white transition">Careers</button></li>
                <li><button onClick={() => navigate('/demo')} className="hover:text-white transition">Platform Demo</button></li>
                <li><button onClick={() => navigate('/live-metrics')} className="hover:text-white transition">Live Metrics</button></li>
                <li><a href="https://infuse-ai.in" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">infuse-ai.in</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-neutral-500">
                <li><a href="mailto:info@infuse.net.in" className="hover:text-white transition">info@infuse.net.in</a></li>
                <li><span>+91-9599960663</span></li>
                <li><span>India</span></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-neutral-500">
              &copy; 2026 Infuse Technologies. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="https://infuse-ai.in" target="_blank" rel="noopener noreferrer" className="text-sm text-neutral-500 hover:text-white transition">
                Privacy
              </a>
              <a href="https://infuse-ai.in" target="_blank" rel="noopener noreferrer" className="text-sm text-neutral-500 hover:text-white transition">
                Terms
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecureSphereLanding;
